/**
 * English. Secondary; the Bulgarian wording in `bg.ts` is the authoritative
 * one, and where the two ever disagree, Bulgarian wins.
 */

import type { MessageKey } from '../../shared/message-keys.ts'
import { SITE_NOTICE } from '../../shared/site-notice.ts'

export const en: Record<MessageKey, string> = {
  // The name is not translated: it is the domain and what people search for.
  // English glosses it once, where a page first names it: Чети по реда ("read in order").
  'app.name': 'Чети по реда',
  'app.tagline': 'Which book do I start with?',
  // The site-wide notice has one home, shared with the emails.
  'app.footer.disclaimer': SITE_NOTICE.en,
  'app.footer.language': 'Български',
  'app.loading': 'Loading…',

  'register.title': 'Create an account',
  'register.lead': 'An email address and a password. We collect nothing else.',
  'register.email.label': 'Email address',
  'register.email.hint': 'We will send a confirmation message to this address.',
  'register.password.label': 'Password',
  'register.password.hint': 'At least 12 characters.',
  'register.nickname.label': 'Nickname',
  'register.nickname.hint': 'Optional. Only you see it, unless you write a review: then it appears beside it.',
  'register.submit': 'Create account',
  'register.submitting': 'Creating…',
  'register.sent.title': 'Check your email',
  'register.sent.body':
    'We have sent a confirmation link. It works once and expires after 24 hours. If it has not arrived, check the spam folder.',
  'register.have_account': 'Already have an account? Sign in.',

  'verify.success.title': 'Address confirmed',
  'verify.success.body': 'You can sign in now.',
  'verify.failed.title': 'That link does not work',
  'verify.failed.body': 'The link has been used or has expired. Ask for a new one and try again.',
  'verify.resend': 'Send a new message',
  'verify.resent': 'If that address exists and is waiting to be confirmed, a new message is on its way.',

  'signin.title': 'Sign in',
  'signin.email.label': 'Email address',
  'signin.password.label': 'Password',
  'signin.submit': 'Sign in',
  'signin.submitting': 'Signing in…',
  'signin.forgot': 'Forgotten your password?',
  'signin.no_account': 'No account? Create one.',

  'forgot.title': 'Forgotten password',
  'forgot.lead': 'Enter your address and we will send a link to set a new password.',
  'forgot.submit': 'Send the link',
  'forgot.sent.title': 'Check your email',
  'forgot.sent.body':
    'If there is an account on that address, a link to set a new password is on its way. It arrives within a few minutes — check your spam folder too. If nothing comes, you probably have no account on that address. The link works once and expires in one hour.',

  'reset.title': 'New password',
  'reset.lead': 'Choose a new password. This signs out every other session.',
  'reset.password.label': 'New password',
  'reset.submit': 'Save the password',
  'reset.success.title': 'Password changed',
  'reset.success.body': 'Sign in with your new password.',

  'account.title': 'Account',
  'account.signed_in_as': 'Signed in as',
  'account.verified': 'Address confirmed.',
  'account.signout': 'Sign out',
  'account.nickname.save': 'Save the nickname',
  'account.delete.title': 'Delete this account',
  'account.delete.warning':
    'Deletion is final. The account, its sessions and everything attached to it go, and cannot be brought back.',
  'account.delete.confirm_label': 'To confirm, type your email address',
  'account.delete.submit': 'Delete the account',
  'account.delete.done': 'The account has been deleted.',

  'nav.label': 'Main navigation',
  'nav.world': 'World',
  'worlds.lead': 'Reading orders for the big fantasy worlds: where to start, how to go on, and which of the books exist in Bulgarian.',
  'worlds.counts': 'Books: {works} · Reading paths: {paths}',
  'worlds.next.title': 'Which world should come next?',
  'worlds.next.body': 'The first world is here.',
  'page.not_found.title': 'No such page',
  'page.not_found.body': 'That address does not lead to a page on this site.',
  'world.not_found.title': 'No such world',
  'world.not_found.body': 'That address does not lead to a world on this site.',
  'world.all': 'All worlds',
  'nav.home': 'Home',
  'nav.series': 'Series',
  'nav.books': 'All books',

  'home.choose': 'Choose a path',
  'home.all_books': 'All {count} books',
  'catalogue.not_loaded.title': 'The catalogue is not loaded yet',
  'catalogue.not_loaded.body': 'The books have not been published yet. Please try again later.',
  'catalogue.error': 'The catalogue could not be loaded just now. Reload the page.',
  'series.index.title': 'The series',
  'series.index.lead': 'Each series reads in its own order. Choose one and start with its first book.',
  'series.reading_order': 'Reading order',
  'series.kicker': 'Path · {stops}',
  'series.stops.one': '{count} stop',
  'series.stops.other': '{count} stops',
  'books.count.one': '{count} book',
  'books.count.other': '{count} books',
  'outside.meta': '{books} · not a path',
  'series.all': 'All series',
  'series.not_found.title': 'No such series',
  'series.not_found.body': 'That address does not lead to a series. Here are all of them:',
  'outside.lead': 'These books are not part of any reading order. Each stands on its own; read it whenever you like.',
  'books.title': 'All books in publication order',
  'book.not_found.title': 'No such book',
  'book.not_found.body': 'That address does not lead to a book. Here are all the books:',
  'book.year': 'First published',
  'book.series': 'Series',
  'book.position': '{position} of {count}',
  'book.path.label': 'Its place in {series}',
  'book.path.nearby': 'The path nearby',
  'book.path.whole': 'The whole path — {stops}',
  'book.publication_order': 'In publication order',
  'book.breadcrumb.stop': 'stop {position} of {count}',
  'book.why': 'Why it is the {ordinal} stop',
  'book.why.numbered': 'Why it is stop {position}',
  'book.outside': 'This book is not part of any reading order — it stands on its own.',
  'book.buy': 'Where to buy it',
  'book.availability.none': 'Not on sale at the moment',
  'book.availability.unknown': 'We have no information on whether it is on sale.',
  'edition.paper.bg': 'Printed edition in Bulgarian',
  'edition.ebook.bg': 'E-book in Bulgarian',
  'edition.audio.bg': 'Audiobook in Bulgarian',
  'edition.paper.en': 'Printed edition in English',
  'edition.ebook.en': 'E-book in English',
  'edition.audio.en': 'Audiobook in English',
  'edition.other': 'Edition',

  'error.invalid_email': 'That does not look like a valid email address.',
  'error.password_too_short': 'The password must be at least 12 characters.',
  'error.password_too_long': 'That password is too long.',
  'error.password_required': 'Enter a password.',
  'error.invalid_credentials': 'Wrong email address or password.',
  'error.email_not_verified': 'That address is not confirmed yet. Check your email.',
  'error.email_taken': 'Check your email.',
  'error.token_invalid': 'That link is not valid.',
  'error.token_expired': 'That link has expired.',
  'error.rate_limited': 'Too many attempts. Try again later.',
  'error.turnstile_failed': 'The check did not pass. Reload the page and try again.',
  'error.turnstile_missing': 'Wait for the check to finish, then try again.',
  'error.message_required': 'Write a message.',
  'error.message_too_long': 'The message is too long.',
  'error.note_too_long': 'The note is too long: 2000 characters at most.',
  'error.nickname_invalid': 'A nickname can be up to 40 characters and cannot contain “@”.',
  'error.review_required': 'Write your review.',
  'error.review_too_long': 'The review is too long: 6000 characters at most.',
  'error.reviews_suspended': 'You cannot write or edit reviews while the suspension lasts.',
  'error.reviews_banned': 'You can no longer write or edit reviews.',
  'error.publish_no_note': 'There is no saved note to publish.',
  'error.publish_too_long': 'The review would be {total} characters, and the limit is {limit}. Shorten the note or the review and try again.',
  'error.publish_conflict': 'Your review or note changed in the meantime. Reload the page and try again.',
  'error.reason_required': 'Write the reason.',
  'error.reason_too_long': 'The reason is too long: 1000 characters at most.',
  'error.not_moderator': 'Only the person running the site can remove reviews.',
  'error.contact_failed': 'The message was not sent. Try again in a moment.',
  'error.verification_mail_failed': 'We could not send the confirmation email. Try again.',

  'terms.title': 'Terms of use',
  'privacy.title': 'Privacy',
  'about.title': 'About the site',
  'contact.title': 'Contact',
  'content.updated': 'Last changed: {date}',
  'content.translation': 'This English text is a translation. The Bulgarian text is authoritative.',
  'contact.lead': 'A mistake in an order, a world to add, or anything else: write to us.',
  'report.link': 'Report a mistake on this page',
  'report.about': 'About: {page}',
  'contact.name.label': 'Name (optional)',
  'contact.email.label': 'Email (optional)',
  'contact.email.hint': 'Only so we can reply to you.',
  'contact.message.label': 'Message',
  'contact.submit': 'Send',
  'contact.sending': 'Sending…',
  'contact.sent.title': 'Your message has been sent',
  'contact.sent.body': 'Thank you. We will reply to the address you left.',
  'contact.sent.body_anonymous': 'Thank you. You left no address, so we cannot reply, but your message will be read.',
  'footer.nav': 'About this site',
  'meta.about.title': 'About the site',
  'meta.about.description': 'What Чети по реда ("read in order") is, how it is made, and who runs it.',
  'error.not_signed_in': 'You need to sign in.',
  'error.confirmation_mismatch': 'That is not the account’s email address.',
  'error.bad_request': 'Something in that request was wrong.',
  'error.unexpected': 'Something went wrong. Try again.',

  // Page metadata. The Worker renders the Bulgarian (og:locale bg_BG); these
  // keep the two tables complete, as the typecheck requires.
  'meta.worlds.title': 'Чети по реда — reading orders for fantasy worlds',
  'meta.worlds.description': 'The great fantasy worlds are rarely read in the order they were written. Here each world has paths: which book comes first, which next, and why.',
  'meta.world.title': '{name} — which book to start with',
  'meta.series_index.title': 'The series — {name}',
  'meta.series.title': '{name} — reading order',
  'meta.books.title': 'All books — {name}',
  'meta.book.title': '{title} — {series}, stop {position} of {count}',
  'meta.quiz.title': 'Which path is yours — a short quiz',
  'meta.quiz.description': 'Ten questions. At the end: where to start, and why there.',
  'reviews.title': 'Readers’ reviews',
  'reviews.none': 'No reviews of this book yet.',
  'reviews.signin': 'Sign in to write a review',
  'reviews.write': 'Write a review',
  'reviews.edit': 'Edit your review',
  'reviews.body.label': 'Your review',
  'reviews.body.hint': 'It is published at once, with your nickname beside it if you have one. No spoilers, unless you tick the box below.',
  'reviews.spoilers.label': 'Contains spoilers',
  'reviews.publish': 'Publish',
  'reviews.cancel': 'Cancel',
  'reviews.delete': 'Delete your review',
  'reviews.suspended': 'You cannot write or edit reviews until {date}, Sofia time, because two of your reviews were removed.',
  'reviews.banned': 'You can no longer write or edit reviews, since a third review of yours was removed.',
  'reviews.anonymous': 'A reader',
  'reviews.yours': 'your review',
  'reviews.spoilers.show': 'Contains spoilers — show the review',
  'reviews.report': 'Report',
  'reviews.report.reason': 'What is wrong (optional)',
  'reviews.report.send': 'Send the report',
  'reviews.report.done': 'Thank you. The report has been sent.',
  'reviews.remove': 'Remove',
  'reviews.remove.reason': 'Reason for removing it',
  'reviews.remove.hint': 'Required. The author receives it by email.',
  'reviews.remove.confirm': 'Remove the review',
  'reviews.remove.done': 'The review was removed and the author told.',
  'reviews.remove.done_unnotified': 'The review was removed, but the email to the author did not go.',
  'nav.quiz': 'Quiz',
  'header.signed_in': 'Signed in:',
  'quiz.kicker': 'Quiz · {count} questions, about two minutes',
  'quiz.title': 'Which series should be your first?',
  'quiz.count': 'You have answered {answered} of {total}',
  'quiz.submit': 'Show the result',
  'quiz.enough': 'That will do — show the result',
  'quiz.too_few': 'Answer at least {min} questions to get a result.',
  'quiz.result.heading': 'Your result',
  'quiz.result.clear': '{first} suits you best. If it does not appeal, try {second}.',
  'quiz.result.close': 'Two series suit you almost equally: {first} and {second}.',
  'quiz.result.tie': 'Two series suit you equally: {first} and {second}.',
  'quiz.result.none': 'Your answers do not point to any one series. Look at them all and choose by the description.',
  'quiz.result.based_on': 'Based on {answered} of {total} answers.',
  'quiz.result.why': 'We chose it because {reasons}.',
  'quiz.result.and': 'and',
  'quiz.result.start': 'Start with {book}.',
  'quiz.result.all': 'Show me all {count}',
  'quiz.share.title': 'Share your result',
  'quiz.share.copy': 'Copy the link',
  'quiz.share.copied': 'Copied',
  'quiz.share.failed': 'Could not copy. Select the link below and copy it.',
  'quiz.share.label': 'Link to this result',
  'meta.quiz.result.title': 'My first series: {first}',
  'meta.quiz.result.description': 'Ten questions on which {world} series to start with. My result: {first}, then {second}.',
  'shelf.title': 'My shelf',
  'shelf.signed_out': 'With an account you can mark which books you want to read, are reading, have read and own, and follow how far you have got in a series.',
  'shelf.load_failed': 'Your shelf did not load. Try again in a moment.',
  'shelf.status.legend': 'Reading',
  'shelf.status.want': 'Want to read it',
  'shelf.status.reading': 'Reading it',
  'shelf.status.read': 'Read it',
  'shelf.status.none': 'None of these',
  'shelf.owned': 'I own it',
  'shelf.rating.legend': 'My rating',
  'shelf.rating.none': 'No rating',
  'shelf.note.label': 'Note',
  'shelf.note.hint': 'Only you can see it.',
  'shelf.saving': 'Saving…',
  'shelf.saved': 'Saved.',
  'shelf.undo': 'Undo',
  'shelf.save_text': 'Save the rating and note',
  'shelf.saved_text': 'Rating and note saved',
  'shelf.saved_rating': 'Rating saved',
  'shelf.saved_note': 'Note saved',
  'shelf.cleared': 'Rating and note cleared',
  'shelf.publish.create': 'Publish the note as a review',
  'shelf.publish.create_hint': 'The note’s text becomes your public review of this book, and the note field is cleared for new notes.',
  'shelf.publish.append': 'Add the note to your review',
  'shelf.publish.append_hint': 'The note is added to the end of your published review, and the note field is cleared for new notes. The review will be {total} of {limit} characters.',
  'shelf.publish.save_first': 'Save the note before publishing it.',
  'shelf.publish.created': 'The note is published as a review. You will find the review further down this page and under “My reviews” in your profile.',
  'shelf.publish.appended': 'The note is added to your review. You will find the review further down this page and under “My reviews” in your profile.',
  'shelf.publish.kept': 'The review is published, but the note was changed in the meantime, so it is kept.',
  'series.progress': 'You have read {read} of {count}.',
  'account.shelf.title': 'My books',
  'account.reviews.title': 'My reviews',
  'account.reviews.empty': 'You have not written a review yet. You write one from the book’s page.',
  'account.shelf.empty': 'You have not marked any books yet. You mark them from each book’s page.',
  'account.shelf.want': 'Want to read',
  'account.shelf.reading': 'Reading',
  'account.shelf.read': 'Read',
  'account.shelf.owned': 'Own',
  'account.shelf.progress': 'How far you have got',
  'account.shelf.read_mark': 'read:',
  'account.quiz.title': 'The quiz',
  'account.quiz.result': 'Your first series: {first}. After it: {second}.',
  'account.quiz.taken': 'Taken on {date}',
  'account.quiz.again': 'Take it again',
  'quiz.result.saved': 'Saved to your account.',
  'meta.terms.title': 'Terms of use',
  'meta.terms.description': 'The terms for using the site.',
  'meta.privacy.title': 'Privacy',
  'meta.privacy.description': 'What data the site collects, and what it does not.',
  'meta.contact.title': 'Contact',
  'meta.contact.description': 'Write to us: a mistake in an order, a world to add, or anything else.',
  'meta.verify.title': 'Confirming your address',
  'meta.not_found.title': 'Page not found',
  'meta.image.alt': 'Чети по реда — reading orders for the great fantasy worlds',
}
